import React, { useState, useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { AuthContext } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import StarRating from './StarRating';

export default function FeedbackModal({ onClose }) {
  const { user } = useContext(AuthContext);

  // ---------- state ----------
  const [format, setFormat] = useState('Text only');
  const [learned, setLearned] = useState(null);
  const [understanding, setUnderstanding] = useState(null);
  const [usefulness, setUsefulness] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [satisfaction, setSatisfaction] = useState(null);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const ratingsReady = [learned, understanding, usefulness, engagement, satisfaction].every((v) => typeof v === 'number');

  // ---------- lock scroll ----------
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => (document.body.style.overflow = prev);
  }, []);

  // ---------- submit ----------
  async function handleSubmit(e) {
    e.preventDefault();
    if (!ratingsReady) return setError('Please rate every question.');

    setSaving(true);
    const { error: dbError } = await supabase.from('user_test_feedback').insert([
      {
        user_id: user.id,
        format_used: format,
        learned,
        understanding,
        usefulness,
        engagement,
        satisfaction,
        comments: comments || null,
      },
    ]);
    setSaving(false);

    if (dbError) {
      console.error(dbError);
      setError(dbError.message || 'Could not save feedback.');
      return;
    }

    // Show thank‑you for a brief moment, then close
    setSubmitted(true);
    setTimeout(() => onClose(), 1500); // 2‑second delay
  }

  // ---------- modal ----------
  const node = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 md:p-10 animate-fade-in">
      {/* Outer Container */}
      <div
        className="relative w-full h-full sm:h-auto sm:w-[32rem] bg-white
                   rounded-none sm:rounded-2xl shadow-xl ring-1 ring-black/10
                   max-h-screen sm:max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none z-10"
        >
          <FaTimes size={20} />
        </button>

        {/* Success overlay */}
        {submitted && (
          <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center gap-3 animate-fade-in z-20">
            <span className="text-green-600 text-5xl">✅</span>
            <p className="text-lg font-semibold text-gray-800">Thank you for your feedback!</p>
          </div>
        )}

        {/* Inner Container */}
        <div
          className="flex-1 overflow-y-auto p-6"
          style={{ scrollbarGutter: 'stable' }}
        >
          {/* heading */}
          <header className="mb-6 flex items-center gap-2">
            <span className="text-2xl">📝</span>
            <h2 className="text-xl font-semibold text-gray-900">Daily Feedback Survey</h2>
          </header>

          {/* form */}
          <form onSubmit={handleSubmit} className="space-y-6 select-none" aria-hidden={submitted}>
            {/* FORMAT RADIO PILLS */}
            <fieldset disabled={submitted}>
              <legend className="text-sm font-medium text-gray-700 mb-2">Which format did you use today?</legend>
              <div className="flex flex-wrap gap-2">
                {['Text only', 'Audio only', 'Both'].map((opt) => {
                  const active = format === opt;
                  return (
                    <label
                      key={opt}
                      className={`cursor-pointer px-3 py-1.5 text-sm rounded-full border transition
                                  ${active ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-800 border-green-200 hover:bg-green-100'}`}
                    >
                      <input
                        type="radio"
                        name="format"
                        value={opt}
                        checked={active}
                        onChange={() => setFormat(opt)}
                        className="sr-only"
                      />
                      {opt}
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* RATINGS */}
            <StarRating label="Did you learn something new today?" value={learned} onChange={setLearned} disabled={submitted} />
            <StarRating label="Did the assistant help you understand the news?" value={understanding} onChange={setUnderstanding} disabled={submitted} />
            <StarRating label="How useful were the suggested follow-up questions?" value={usefulness} onChange={setUsefulness} disabled={submitted} />
            <StarRating label="How engaging was today’s experience?" value={engagement} onChange={setEngagement} disabled={submitted} />
            <StarRating label="Overall, how satisfied are you?" value={satisfaction} onChange={setSatisfaction} disabled={submitted} />

            {/* COMMENTS */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Anything you'd like to share? <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                rows="3"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                className="w-full resize-none rounded-lg border border-gray-200 p-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Your thoughts…"
                disabled={submitted}
              />
            </div>

            {/* ERROR */}
            {error && <p className="text-sm text-red-600" aria-live="polite">{error}</p>}

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={saving || !ratingsReady || submitted}
              className="w-full inline-flex items-center justify-center gap-2 bg-green-600 text-white font-medium py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Submit Feedback'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
}

/* tiny global animation helper (once) */
if (!document.getElementById('feedback-fade-style')) {
  const style = document.createElement('style');
  style.id = 'feedback-fade-style';
  style.textContent = `
    @keyframes fade-in { from { opacity:0; transform:scale(.98);} to { opacity:1; transform:scale(1);} }
    .animate-fade-in { animation:fade-in .15s ease-out; }
  `;
  document.head.appendChild(style);
}
